import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AppContext from '../../../../contexts/AppContext'
import {
  fetchCharacterInventory,
  selectCharacter,
} from '../../ViewInventory/inventorySlice'
import {
  fetchSheetReview,
  resubmitSheetReview,
  updateSheetReviewFields,
} from '../../../../services/sheetReview.api'

export const STAT_EDITOR_KEYS = [
  { key: 'strength', label: 'Fuerza' },
  { key: 'dexterity', label: 'Destreza' },
  { key: 'agility', label: 'Agilidad' },
  { key: 'resistance', label: 'Resistencia' },
  { key: 'willpower', label: 'Voluntad' },
  { key: 'chakraControl', label: 'Control de Chakra' },
  { key: 'concentration', label: 'Concentración' },
  { key: 'intelligence', label: 'Inteligencia' },
]

function emptyStatsDraft() {
  const o = {}
  for (const { key } of STAT_EDITOR_KEYS) o[key] = 0
  return o
}

function normalizeStatsDraft(value) {
  const base = emptyStatsDraft()
  let src = value
  if (typeof src === 'string') {
    try {
      src = JSON.parse(src)
    } catch {
      return base
    }
  }
  if (!src || typeof src !== 'object' || Array.isArray(src)) return base
  for (const { key } of STAT_EDITOR_KEYS) {
    const n = Number(src[key])
    base[key] = Number.isFinite(n) ? Math.min(10, Math.max(0, Math.trunc(n))) : 0
  }
  return base
}

function fieldValueToDraft(fieldKey, value) {
  if (fieldKey === 'stats') return normalizeStatsDraft(value)
  if (value == null) return ''
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

/**
 * Estado y acciones de Corregir ficha para dueño (banner / panel / gates Historia).
 * @param {string} slug
 */
export default function useSheetReview(slug) {
  const dispatch = useDispatch()
  const { user } = useContext(AppContext)
  const character = useSelector(selectCharacter)

  const [review, setReview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [fieldDrafts, setFieldDrafts] = useState({})
  const [savingField, setSavingField] = useState(null)
  const [resubmitting, setResubmitting] = useState(false)
  const dirtyKeysRef = useRef(new Set())

  const loadReview = useCallback(async () => {
    if (!slug) return null
    setLoading(true)
    try {
      const data = await fetchSheetReview(slug)
      setReview(data)
      return data
    } catch {
      setReview(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    dirtyKeysRef.current = new Set()
    setFieldDrafts({})
    loadReview()
  }, [loadReview])

  useEffect(() => {
    if (!review?.values) return
    setFieldDrafts((prev) => {
      const next = { ...prev }
      const needsChange = new Set()
      for (const f of review.fields || []) {
        if (f.status !== 'needs_change') continue
        needsChange.add(f.fieldKey)
        if (!dirtyKeysRef.current.has(f.fieldKey)) {
          next[f.fieldKey] = fieldValueToDraft(f.fieldKey, review.values[f.fieldKey])
        }
      }
      for (const key of Object.keys(next)) {
        if (!needsChange.has(key)) delete next[key]
      }
      return next
    })
  }, [review])

  const isOwner = useMemo(
    () => Number(character?.userId) === Number(user?.userId),
    [character?.userId, user?.userId]
  )

  const sheetStatus = review?.sheetStatus || null
  const enabled = review?.enabled !== false
  const showReviewUi = isOwner && review && enabled

  const canEdit =
    isOwner && sheetStatus !== 'approved' && sheetStatus !== 'in_review' && enabled

  const canResubmit =
    isOwner && (sheetStatus === 'draft' || sheetStatus === 'needs_correction') && enabled

  const fieldStatusByKey = useMemo(() => {
    const map = {}
    for (const f of review?.fields || []) {
      map[f.fieldKey] = f.status
    }
    return map
  }, [review])

  const needsChangeKeys = useMemo(
    () => (review?.fields || []).filter((f) => f.status === 'needs_change').map((f) => f.fieldKey),
    [review]
  )

  const setFieldDraft = useCallback((fieldKey, value) => {
    dirtyKeysRef.current.add(fieldKey)
    setFieldDrafts((prev) => ({ ...prev, [fieldKey]: value }))
  }, [])

  const setStatDraft = useCallback((statKey, raw) => {
    dirtyKeysRef.current.add('stats')
    const n = Number(raw)
    const qty = Number.isFinite(n) ? Math.min(10, Math.max(0, Math.trunc(n))) : 0
    setFieldDrafts((prev) => ({
      ...prev,
      stats: { ...normalizeStatsDraft(prev.stats), [statKey]: qty },
    }))
  }, [])

  const saveField = useCallback(
    async (fieldKey) => {
      if (!slug || !canEdit) return
      setSavingField(fieldKey)
      setError('')
      try {
        let value = fieldDrafts[fieldKey]
        if (fieldKey === 'stats') {
          value = normalizeStatsDraft(value)
        }
        if (fieldKey === 'age' && value !== '' && value != null) {
          value = Number(value)
        }
        const data = await updateSheetReviewFields(slug, { [fieldKey]: value })
        dirtyKeysRef.current.delete(fieldKey)
        setReview(data)
        await dispatch(fetchCharacterInventory(slug))
        return data
      } catch (err) {
        setError(err?.message || 'No se pudo guardar el campo.')
        throw err
      } finally {
        setSavingField(null)
      }
    },
    [slug, canEdit, fieldDrafts, dispatch]
  )

  const resubmit = useCallback(async () => {
    if (!slug || !canResubmit) return
    setResubmitting(true)
    setError('')
    try {
      const data = await resubmitSheetReview(slug)
      dirtyKeysRef.current = new Set()
      setReview(data)
      return data
    } catch (err) {
      setError(err?.message || 'No se pudo reenviar a revisión.')
      throw err
    } finally {
      setResubmitting(false)
    }
  }, [slug, canResubmit])

  return {
    review,
    loading,
    error,
    setError,
    isOwner,
    sheetStatus,
    enabled,
    showReviewUi,
    canEdit,
    canResubmit,
    fieldStatusByKey,
    needsChangeKeys,
    fieldDrafts,
    setFieldDraft,
    setStatDraft,
    savingField,
    resubmitting,
    loadReview,
    saveField,
    resubmit,
  }
}
